// The chat line an owner's Undo of Neo's change writes (TOM-662, cloud ADR
// 0392). Every undo route's 200 answer carries an optional `chatNote` when it
// wrote one into the chat whose turn made the change. Each undo request
// announces it on `window` as it reads the answer, wherever the Undo was
// pressed (a receipt in the chat, the board page), and an open
// chat on that conversation adds it as a small plain line, not a message from
// Neo, the way a reload draws the stored `owner_undo` row (neoChatHistory.ts). The text is the
// server's; the web never builds its own.
//
// Pure, apart from announceUndoChatNote.

import type { NeoUndoChatNote } from '@neomaris/protocol';

/** Fired on `window` with a `NeoUndoChatNote` as its detail when an undo wrote a chat line. */
export const NEO_UNDO_NOTE_EVENT = 'nm:neo-undo-note';

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function nonEmpty(v: unknown): v is string {
  return typeof v === 'string' && v.length > 0;
}

/** An undo answer's `chatNote`, or null when it has none or a malformed one. */
export function parseUndoChatNote(body: unknown): NeoUndoChatNote | null {
  const note = isRecord(body) ? body.chatNote : undefined;
  if (!isRecord(note)) return null;
  const { conversationId, messageId, text } = note;
  if (!nonEmpty(conversationId) || !nonEmpty(messageId) || !nonEmpty(text)) return null;
  return { conversationId, messageId, text };
}

/** Tell an open chat about the line an undo answer carries, if it carries one. */
export function announceUndoChatNote(body: unknown): void {
  const note = parseUndoChatNote(body);
  if (note === null || typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(NEO_UNDO_NOTE_EVENT, { detail: note }));
}

/** The line a chat adds for an undo's note: a reload draws its stored row the same way. */
export interface UndoChatNoteLine {
  id: string;
  role: 'assistant';
  content: string;
  local: true;
  /** Drawn as a small plain line, not as a message from Neo. */
  undoNote: true;
}

/**
 * The line to add under the chat's messages for the note, keyed by its message
 * id, `local`, with no thumbs, or null: the note is for another conversation,
 * or the chat already holds it (a retried undo replayed, a re-read history).
 */
export function undoChatNoteLine(
  current: readonly { id: string }[],
  note: NeoUndoChatNote,
  conversationId: string | null
): UndoChatNoteLine | null {
  if (conversationId === null || note.conversationId !== conversationId) return null;
  if (current.some((m) => m.id === note.messageId)) return null;
  return {
    id: note.messageId,
    role: 'assistant',
    content: note.text,
    local: true,
    undoNote: true,
  };
}
